import {graphql} from '@apollo/client/react/hoc';
import log from 'core/libs/logger';
import gql from 'graphql-tag.macro';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {compose, setPropTypes, withProps} from 'recompose';
import withDelightedSurvey from './withDelightedSurvey';

const ALL_STEPS_IN_TRACK = gql`
  query allStepsInTrack($id: ID!) {
    course(id: $id) {
      id
      trackstepSet {
        edges {
          node {
            id
            track {
              id
              slug
              name
              degree {
                id
              }
              trackstepSet {
                edges {
                  node {
                    id
                    course {
                      id
                      slug
                      name
                      stageSet {
                        edges {
                          node {
                            id
                            slug
                            name
                            stepSet {
                              edges {
                                node {
                                  id
                                  slug
                                  name
                                  status
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

function createUrl(track, course, stage, step, isDegree = false) {
  return `${isDegree ? '/degrees' : ''}/${track.slug}/${course.slug}/${
    stage.slug
  }/${step.slug}`;
}

const withNextStep = compose(
  injectIntl,

  setPropTypes({
    courseId: PropTypes.number.isRequired,
    stepId: PropTypes.number.isRequired,
    trackId: PropTypes.number.isRequired,
  }),

  withProps(() => ({nextStep: {}})),

  graphql(ALL_STEPS_IN_TRACK, {
    alias: 'withAllStepsInTrack',
    options: ({courseId}) => ({
      variables: {
        id: btoa(`CourseType:${courseId}`),
      },
    }),
    props: ({data, ownProps: {intl, stepId, trackId}}) => {
      const {course, error, loading} = data;
      if (loading) return {loading};
      if (error) return {error};

      const encodedStepId = btoa(`StepType:${stepId}`);
      const courseId = course && course.id;
      const encodedTrackId = btoa(`TrackType:${trackId}`);
      const tracks = course && course.trackstepSet && course.trackstepSet.edges;
      let track =
        tracks &&
        tracks.find(
          ({
            node: {
              track: {id},
            },
          }) => id === encodedTrackId,
        );
      if (track) {
        track = track.node && track.node.track; // eslint-disable-line prefer-destructuring
      } else {
        log('Could not find the track for', {
          trackId,
          courseId,
          stepId,
          currentUrl: typeof window !== 'undefined' && window.location.href,
        });
        track = tracks[0].node.track; // eslint-disable-line prefer-destructuring
      }
      const isDegree = !!track.degree;
      const courses = track && track.trackstepSet && track.trackstepSet.edges;
      const currentCourseIndex =
        courses &&
        courses.findIndex(
          ({node}) => (node && node.course && node.course.id) === courseId,
        );
      const currentCourse =
        courses[currentCourseIndex] &&
        courses[currentCourseIndex].node &&
        courses[currentCourseIndex].node.course;

      const stages =
        currentCourse && currentCourse.stageSet && currentCourse.stageSet.edges;
      const currentStageIndex =
        stages &&
        stages.findIndex(({node}) => {
          return (
            node &&
            node.stepSet &&
            node.stepSet.edges &&
            node.stepSet.edges.some(({node: step}) => {
              return step.id === encodedStepId;
            })
          );
        });

      if (currentStageIndex === -1) {
        return {nextStep: {}};
      }

      const currentStage =
        stages[currentStageIndex] && stages[currentStageIndex].node;

      const steps =
        currentStage && currentStage.stepSet && currentStage.stepSet.edges;
      const currentStepIndex =
        steps &&
        steps.findIndex(({node}) => (node && node.id) === encodedStepId);

      let isTheFifthStepInTheFirstCourse = false;

      if (
        currentCourseIndex === 0 &&
        currentStageIndex === 0 &&
        currentStepIndex === 4 &&
        !isDegree
      ) {
        // Set delighted query string to display the survey
        const params = new URLSearchParams(window.location.search);
        params.set('delighted', 'test');
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${params}`,
        );
        isTheFifthStepInTheFirstCourse = true;
      }

      // The next step is in the same stage
      if (currentStepIndex < steps.length - 1) {
        const nextStep =
          steps[currentStepIndex + 1] && steps[currentStepIndex + 1].node;
        return {
          isTheFifthStepInTheFirstCourse,
          nextStep: {
            text: nextStep.name,
            url: createUrl(
              track,
              currentCourse,
              currentStage,
              nextStep,
              isDegree,
            ),
          },
        };
      }
      try {
        // The next step is in the current course
        if (currentStageIndex < stages.length - 1) {
          const nextStage =
            stages[currentStageIndex + 1] && stages[currentStageIndex + 1].node;
          const nextStep =
            nextStage &&
            nextStage.stepSet &&
            nextStage.stepSet.edges[0] &&
            nextStage.stepSet.edges[0].node;
          return {
            nextStep: {
              text: nextStep.name,
              url: createUrl(
                track,
                currentCourse,
                nextStage,
                nextStep,
                isDegree,
              ),
            },
          };
        }

        // The last step of a course
        if (currentCourseIndex < courses.length - 1) {
          const nextCourse =
            courses[currentCourseIndex + 1] &&
            courses[currentCourseIndex + 1].node &&
            courses[currentCourseIndex + 1].node.course;
          const nextStage =
            nextCourse &&
            nextCourse.stageSet &&
            nextCourse.stageSet.edges[0] &&
            nextCourse.stageSet.edges[0].node;
          const nextStep =
            nextStage &&
            nextStage.stepSet &&
            nextStage.stepSet.edges[0] &&
            nextStage.stepSet.edges[0].node;
          return {
            isLastStepOfACourse: true,
            nextStep: {
              text: nextStep.name,
              url: createUrl(track, nextCourse, nextStage, nextStep, isDegree),
            },
          };
        }
      } catch (e) {
        log('We have a problem calculating the next step for:', {
          trackId,
          courseId,
          stepId,
          currentUrl: typeof window !== 'undefined' && window.location.href,
        });
      }

      // End of track
      return {
        nextStep: isDegree
          ? {
              isLastStepOfACourse: true,
              text: intl.formatMessage({id: 'choose_next_degree'}),
              url: '/degrees',
            }
          : {
              isLastStepOfACourse: true,
              text: intl.formatMessage({id: 'choose_next_track'}),
              url: '/tracks',
            },
      };
    },
  }),

  withDelightedSurvey,
);

export default withNextStep;
